import cn from 'classnames';

import { BaseInput } from '../../../base-input';

import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactElement } from 'react';

import type { SliderNumberInputAttributes } from '../../slider/slider.types';

/** Props for the visible `<input type="number">` rendered when `withNumberInput` is enabled. */
export interface SliderNumberInputProps {
  ariaLabel?: string;
  ariaLabelledby?: string;
  disabled?: boolean;
  error?: boolean;
  inputProps?: SliderNumberInputAttributes;
  max: number;
  min: number;
  onBlur: (event: FocusEvent<HTMLInputElement>) => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  step: 'any' | number;
  value: string;
}

/**
 * A compact `<input type="number">` rendered alongside the slider when `withNumberInput`
 * is enabled. Allows users to type an exact value instead of dragging.
 *
 * For range sliders, two instances are rendered (one per thumb). The input respects
 * all slider constraints (min, max, step, marks, snap) via the parent's change handlers.
 *
 * Merges any user-provided `inputProps` (including custom `onBlur`/`onChange` handlers)
 * with the slider's internal handlers.
 */
export const SliderNumberInput = ({
  ariaLabel,
  ariaLabelledby,
  disabled,
  error,
  inputProps,
  max,
  min,
  onBlur,
  onChange,
  onKeyDown,
  readOnly,
  step,
  value,
}: SliderNumberInputProps): ReactElement => {
  const {
    className,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    onKeyDown: onKeyDownProp,
    ...restInputProps
  } = inputProps ?? {};

  return (
    <BaseInput
      className={cn('rbui-slider__number-input', className, {
        'rbui-slider__number-input--error': error,
      })}
      disabled={disabled}
      error={error}
      inputProps={{
        ...restInputProps,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledby,
        max,
        min,
        onKeyDown: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const inputEvent = event as KeyboardEvent<HTMLInputElement>;
          onKeyDown(inputEvent);
          onKeyDownProp?.(inputEvent);
        },
        step,
        type: 'number',
      }}
      onBlur={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const inputEvent = event as FocusEvent<HTMLInputElement>;
        onBlur(inputEvent);
        onBlurProp?.(inputEvent);
      }}
      onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const inputEvent = event as ChangeEvent<HTMLInputElement>;
        onChange(inputEvent);
        onChangeProp?.(inputEvent);
      }}
      readOnly={readOnly}
      value={value}
    />
  );
};

SliderNumberInput.displayName = 'SliderNumberInput';
